// ConceptServices.js : Ce module contient les fonctions utilitaires et services partagés

import { makeAuthenticatedRequest } from '../../components/APIService';

// URL de base pour les requêtes API
const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
 
// Fonction pour normaliser le nom du concept
export const getConceptName = (conceptItem) => {
  if (!conceptItem) return 'Concept inconnu';
  
  // Si c'est une chaîne
  if (typeof conceptItem === 'string') {
    return conceptItem;
  }
  
  // Si c'est un objet
  return conceptItem.concept || conceptItem.value || conceptItem.title || 'Concept sans nom';
};

// Fonction pour inférer une catégorie de concept
export const inferConceptCategory = (title, doc, description) => {
  // Inférer à partir de mots-clés dans le titre du concept
  const educationTerms = ['apprentissage', 'éducation', 'formation', 'enseignement', 'pédagogie'];
  const techTerms = ['algorithme', 'technologie', 'système', 'données', 'numérique', 'informatique'];
  const businessTerms = ['gestion', 'finance', 'marketing', 'stratégie', 'entreprise', 'économie'];
  
  const titleLower = title.toLowerCase();
  
  if (educationTerms.some(term => titleLower.includes(term))) {
    return 'Éducation';
  } else if (techTerms.some(term => titleLower.includes(term))) {
    return 'Technologie';
  } else if (businessTerms.some(term => titleLower.includes(term))) {
    return 'Business';
  }
  
  // Par défaut, affecter une catégorie générique
  return 'Général';
};

// Fonction pour récupérer les concepts depuis l'API
export const fetchConceptsData = async (userId, competenceId) => {
  if (!userId || !competenceId) {
    return { concepts: [], descriptions: [] };
  }
  
  try {
    // Utiliser la nouvelle API /api/getConcepts qui retourne les concepts avec documents complets
    const response = await makeAuthenticatedRequest(
      `${BackEndUrl}/api/getConcepts?objectiveId=${competenceId}`
    );
    
    const data = await response.json();

    if (!data.success || !data.concepts) {
      throw new Error('Erreur lors de la récupération des concepts');
    }

    // Regrouper les concepts par nom (un concept peut apparaître dans plusieurs documents)
    const conceptsMap = {};
    
    data.concepts.forEach(conceptData => {
      const conceptName = conceptData.concept;
      const doc = conceptData.document; // Document complet fourni par l'API
      
      if (!conceptsMap[conceptName]) {
        conceptsMap[conceptName] = {
          title: conceptName,
          concept: conceptName,
          count: 1,
          documents: doc ? [doc] : [],
          source: conceptData.source || 'upload',
          searchQuery: conceptData.searchQuery || null,
          relevance: doc?.score || 0.5,
          category: inferConceptCategory(conceptName, doc, conceptData)
        };
      } else {
        conceptsMap[conceptName].count++;
        // Ajouter le document s'il n'est pas déjà présent
        if (doc && !conceptsMap[conceptName].documents.find(d => d.id === doc.id)) {
          conceptsMap[conceptName].documents.push(doc);
        }
        // Recalculer la pertinence moyenne
        conceptsMap[conceptName].relevance = conceptsMap[conceptName].documents.reduce(
          (sum, d) => sum + (d.score || 0.5), 0
        ) / conceptsMap[conceptName].documents.length;
      }
    });

    // Convertir la map en liste
    const conceptsList = Object.values(conceptsMap);

    // Créer les descriptions depuis les concepts retournés par l'API
    const descriptions = data.concepts.map(c => ({
      concept: c.concept,
      description: c.description,
      resume: c.resume,
      résumé: c.resume, // Compatibilité avec l'ancien format
      extraits: c.extraits,
      pages: c.pages,
      relatedConcepts: c.relatedConcepts || []
    }));

    return { 
      concepts: conceptsList, 
      descriptions: descriptions 
    };
    
  } catch (error) {
    console.error('Erreur lors de la récupération des concepts:', error);
    return { concepts: [], descriptions: [], error };
  }
};

// Fonction pour filtrer les concepts selon des critères
export const filterConcepts = (concepts, searchQuery, hasContextFilter, categoriesFilter, conceptDescriptions) => {
  return concepts
    .filter(concept => {
      // Filtre par recherche textuelle - chercher dans concept et title
      const matchesSearch = searchQuery === '' || 
        (concept.title && concept.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (concept.concept && concept.concept.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Filtre par disponibilité de résumé
      const matchesContextFilter = hasContextFilter === null ||
        (hasContextFilter === true && conceptDescriptions.some(desc => 
          (desc.concept === concept.title || desc.concept === concept.concept) && 
          (desc.resume || desc.résumé)
        )) ||
        (hasContextFilter === false && !conceptDescriptions.some(desc => 
          (desc.concept === concept.title || desc.concept === concept.concept) && 
          (desc.resume || desc.résumé)
        ));
          
      // Filtre par catégories
      const matchesCategory = categoriesFilter.length === 0 ||
        categoriesFilter.includes(concept.category);
        
      return matchesSearch && matchesContextFilter && matchesCategory;
    });
};

// Fonction pour trier les concepts
export const sortConcepts = (concepts, groupBy) => {
  return [...concepts].sort((a, b) => {
    if (groupBy === 'alphabetical') {
      return a.title.localeCompare(b.title);
    } else if (groupBy === 'count') {
      return b.count - a.count;
    } else if (groupBy === 'category') {
      return (a.category || '').localeCompare(b.category || '');
    } else { // 'relevance' (default)
      return b.relevance - a.relevance;
    }
  });
};

// Fonction pour regrouper les concepts par catégorie
export const groupConceptsByCategory = (concepts) => {
  const categories = {};
  
  concepts.forEach(concept => {
    const category = concept.category || 'Non classé';
    if (!categories[category]) {
      categories[category] = [];
    }
    categories[category].push(concept);
  });
  
  return categories;
};

// Fonction pour extraire les catégories disponibles
export const extractAvailableCategories = (concepts) => {
  const categories = new Set();
  concepts.forEach(concept => {
    if (concept.category) {
      categories.add(concept.category);
    }
  });
  return Array.from(categories).sort();
};
